import { useEffect, useRef, useState } from 'react';

/**
 * Count up from 0 to `target` using ease-out cubic, but only AFTER the
 * referenced element scrolls into view. Animation runs once per mount.
 *
 * Returns { ref, display, complete }:
 *   - ref:     attach to the element you want to watch
 *   - display: current number (integer) — bind to your UI
 *   - complete: true after the animation finishes (use to sync to live values)
 *
 * Honours prefers-reduced-motion: jumps straight to the target value.
 */
export function useCountUpOnVisible(target, durationMs = 1500, threshold = 0.5) {
  const ref = useRef(null);
  const [display, setDisplay] = useState(0);
  const [complete, setComplete] = useState(false);
  const playedRef = useRef(false);
  const rafRef = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;

    const reduceMotion =
      typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const startAnimation = () => {
      if (playedRef.current) return;
      playedRef.current = true;

      if (reduceMotion) {
        setDisplay(target);
        setComplete(true);
        return;
      }

      const startTime = performance.now();
      const tick = (now) => {
        const t = Math.min((now - startTime) / durationMs, 1);
        const eased = 1 - Math.pow(1 - t, 3); // ease-out-cubic
        setDisplay(Math.round(eased * target));
        if (t < 1) {
          rafRef.current = requestAnimationFrame(tick);
        } else {
          setComplete(true);
        }
      };
      rafRef.current = requestAnimationFrame(tick);
    };

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) startAnimation();
        });
      },
      { threshold }
    );
    io.observe(el);

    return () => {
      io.disconnect();
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [target, durationMs, threshold]);

  return { ref, display, complete };
}
